import { useState, type ClipboardEvent, type ReactNode } from 'react';
import { apiPost } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { isDtStaff } from '../lib/staff';
import { filesFromPaste } from '../lib/attachUpload';
import { AttachButton, UploadFeedback, useAttachmentUpload, useFileDrop } from './AttachmentUploader';

interface Props {
  displayId: number;
  onPosted: () => void;
  /**
   * Ajout de pièces jointes depuis la zone de réponse (trombone, glisser-
   * déposer, collage d'une capture). Mêmes règles que le bloc Pièces jointes :
   * interrupteur attach_upload, point non figé. Les pièces vont au point.
   */
  canAttach?: boolean;
  onAttached?: () => void;
  /** Colonne de droite de la carte (liste des pièces jointes du point). */
  aside?: ReactNode;
}

const noop = () => {};

export function MessageComposer({ displayId, onPosted, canAttach = false, onAttached = noop, aside }: Props) {
  const { user } = useAuth();
  const [text, setText] = useState('');
  const [portee, setPortee] = useState<'public' | 'interne'>('public');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const attach = useAttachmentUpload(displayId, onAttached);
  const { dragging, dropProps } = useFileDrop((files) => void attach.upload(files), canAttach && !attach.busy);

  if (!user) return null;
  // Message interne : agents Digital Team seulement (l'API ramène le reste à public).
  const isStaff = isDtStaff(user);

  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await apiPost(`/api/tickets/${displayId}/messages`, { message: text, portee });
      setText('');
      onPosted();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  // Coller une capture d'écran (Ctrl+V) : ajoutée aux pièces jointes. Un
  // collage qui porte du texte (Excel, Word…) reste un collage de texte.
  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    if (!canAttach || attach.busy) return;
    const files = filesFromPaste(Array.from(e.clipboardData.files), e.clipboardData.getData('text/plain'));
    if (files.length === 0) return;
    e.preventDefault();
    void attach.upload(files);
  };

  return (
    <div
      className={`composer${dragging ? ' is-dragging' : ''}`}
      {...dropProps}
      style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px', marginTop: 12 }}
    >
      <div className={aside ? 'composer-grid' : undefined}>
        <div className="composer-main">
          {isStaff && (
            <div style={{ display: 'flex', gap: 4, background: 'var(--bg-soft)', borderRadius: 6, padding: 3, marginBottom: 10, width: 'fit-content' }}>
              <ToggleBtn active={portee === 'public'} onClick={() => setPortee('public')}>
                🌐 Public (client + watchers)
              </ToggleBtn>
              <ToggleBtn active={portee === 'interne'} onClick={() => setPortee('interne')}>
                🔒 Interne (staff DT)
              </ToggleBtn>
            </div>
          )}
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onPaste={onPaste}
            aria-label="Votre message"
            placeholder={
              isStaff
                ? portee === 'interne'
                  ? 'Note interne — non visible du client. Code, SQL, copier-coller acceptés.'
                  : 'Réponse publique au client. Markdown supporté.'
                : 'Votre réponse…'
            }
            style={{
              width: '100%',
              minHeight: 80,
              // Meme bordure que les autres champs (index.css : --border-strong),
              // sauf en note interne, signalee par la couleur d'avertissement.
              border: `1px solid ${portee === 'interne' ? 'var(--warning-border)' : 'var(--border-strong)'}`,
              background: portee === 'interne' ? 'var(--warning-soft)' : 'var(--bg)',
              borderRadius: 6,
              padding: '8px 10px',
              fontSize: 'var(--fs-sm)',
              fontFamily: 'inherit',
              resize: 'vertical',
            }}
            disabled={busy}
          />
          {err && (
            <div style={{ marginTop: 8, color: 'var(--danger)', fontSize: 'var(--fs-xs)' }}>
              {err}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            {canAttach && (
              <>
                <AttachButton onFiles={(f) => void attach.upload(f)} disabled={attach.busy} label="Joindre un fichier" />
                <span className="composer-hint">ou glissez-déposez, ou collez une capture</span>
              </>
            )}
            <div style={{ flex: 1 }} />
            <button className="btn primary" onClick={submit} disabled={busy || !text.trim()} style={{ fontSize: 'var(--fs-xs)' }}>
              {busy ? 'Envoi…' : portee === 'interne' ? '🔒 Publier note interne' : 'Envoyer la réponse'}
            </button>
          </div>
          {canAttach && <UploadFeedback busy={attach.busy} feedback={attach.feedback} />}
        </div>
        {aside && <div className="composer-aside">{aside}</div>}
      </div>
      {dragging && (
        <div className="composer-drop-hint" aria-hidden="true">
          Déposez pour ajouter aux pièces jointes du point
        </div>
      )}
    </div>
  );
}

function ToggleBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: active ? 'var(--accent)' : 'transparent',
        color: active ? '#fff' : 'var(--text-mute)',
        border: 0,
        borderRadius: 4,
        padding: '4px 12px',
        fontSize: 'var(--fs-xs)',
        fontWeight: active ? 600 : 400,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}
